import React, { useCallback, useEffect, useState } from 'react';
import {
  Row, Col, Container, Card, Spinner, Button, Popover, OverlayTrigger, ListGroup, InputGroup, Form
} from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  previewMailer,
  fetchCallListItems,
  getMailersHeader
} from 'bundles/connect/helpers/prospect_api';
import type { CallList, CallListItem, ConnectProperty, MailersHeader, MailersTemplate } from 'bundles/connect/types';
import PreviewBody from './body';
import { faArrowLeft, faArrowRight, faCaretDown, faDownload, faSearch } from '@fortawesome/pro-solid-svg-icons';
import { debounce } from 'lodash';
import PreviewHeader from './header';
import MailersTemplatesHeader from '../templates/mailers_templates_header';
import DownloadModal from './single_download_modal';

type MailersTemplatesPreviewProps = {
  callList: CallList;
  callListLength: number;
  initialCallListItemId: number;
  startingIndex: number;
  template: MailersTemplate;
  property: ConnectProperty;
}

export default function MailersTemplatesPreview(
  {
    callList,
    callListLength,
    initialCallListItemId,
    property,
    startingIndex,
    template,
  }: MailersTemplatesPreviewProps
) {
  const [previewBody, setPreviewBody] = useState<string | null>(null);
  const [currentProperty, setCurrentProperty] = useState(property);
  const [currentCallListItemId, setCurrentCallListItemId] = useState(initialCallListItemId);
  const [showPopover, setShowPopover] = useState<boolean>(false);
  const [index, setIndex] = useState(startingIndex);
  const [callListItems, setCallListItems] = useState<CallListItem[]>([]);
  const [loadingCallListItems, setLoadingCallListItems] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [header, setHeader] = useState<MailersHeader | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    setPreviewBody(null);
    Promise.all([
      getMailersHeader(),
      previewMailer(callList.id, template.id, { item_id: initialCallListItemId })
    ]).then(([headerData, { body, index }]) => {
      setHeader(headerData);
      setPreviewBody(body);
      setIndex(index);
      setInitialLoad(false);
    });
  }, []);

  const debouncedSearch = useCallback(
    debounce((query) => {
      search(query);
    }, 300),
    []
  );

  const search = (query: string) => {
    const params = {
      'q[call_listable_of_Connect::Property_type_address_cont]': query
    };
    setLoadingCallListItems(true);
    fetchCallListItems(params, callList.id).then((data) => {
      setCallListItems(data.data);
      setLoadingCallListItems(false);
    });
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(event.target.value);
  };

  const handleDropdownClick = () => {
    if (callListItems.length === 0) {
      search('');
    }
  };

  const handleItemChange = (step: number) => {
    setPreviewBody(null);
    previewMailer(callList.id, template.id, { index: index + step }).then(({ body, index, itemId, property }) => {
      setPreviewBody(body);
      setIndex(index);
      setCurrentCallListItemId(itemId);
      setCurrentProperty(property);
    });
  };

  const handleItemSelect = (itemId: number) => {
    setShowPopover(false);
    setPreviewBody(null);
    previewMailer(callList.id, template.id, { item_id: itemId })
      .then(({ body, index, itemId, property }) => {
        setPreviewBody(body);
        setIndex(index);
        setCurrentCallListItemId(itemId);
        setCurrentProperty(property);
      });
  };

  const renderCallListItems = () => {
    if (loadingCallListItems) {
      return (
        <div className="text-center p-3">
          <Spinner animation="border" variant="secondary" />
        </div>
      );
    }

    return (
      <ListGroup variant="flush">
        {callListItems.length > 0 ? (
          callListItems.map((item, itemIndex) => (
            <ListGroup.Item
              action
              active={item.id === currentCallListItemId}
              className="d-flex flex-grow-1"
              key={itemIndex}
              onClick={() => handleItemSelect(item.id)}
            >
              {item.callListable.name}
            </ListGroup.Item>
          ))
        ) : (
          <ListGroup.Item className="d-flex flex-grow-1" disabled>No properties found</ListGroup.Item>
        )}
      </ListGroup>
    );
  };

  return (
    <div className="position-absolute w-100 h-100 d-flex flex-column">
      <div className="d-flex flex-column flex-grow-1 overflow-hidden">
        <MailersTemplatesHeader
          callListId={callList.id}
          templateId={template.id}
          templateTitle={template.title}
        />

        <Container className="p-6 flex-grow-1 overflow-hidden">
          <Card className="h-100">
            <Card.Body className="d-flex flex-column h-100">
              <div className="d-flex justify-content-end gap-2 p-4">
                <div className="d-flex flex-column flex-grow-1">
                  <div className="fs-large fw-bold">{currentProperty.address}</div>
                  <span className="text-muted">
                    {currentProperty.city}, {currentProperty.state} {currentProperty.zip}
                  </span>
                </div>
                <Button variant="text-primary" onClick={() => handleItemChange(-1)}>
                  <FontAwesomeIcon icon={faArrowLeft} />
                  Previous
                </Button>
                <div className="text-muted">
                  <OverlayTrigger
                    overlay={
                      <Popover id="properties-popover" style={{ maxWidth: '500px' }}>
                        <Popover.Body className="p-0" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                          <Card>
                            <Card.Header>
                              <Row>
                                <Col xs={6}>
                                  <h6>{callList.name}</h6>
                                </Col>
                                <Col className="text-end" xs={6}>
                                  <div className="text-muted">{callListLength} Properties</div>
                                </Col>
                                <Col xs={12}>
                                  <InputGroup className="flex-nowrap">
                                    <InputGroup.Text><FontAwesomeIcon icon={faSearch} /></InputGroup.Text>
                                    <Form.Control
                                      placeholder="Search for address"
                                      onChange={handleChange}
                                    />
                                  </InputGroup>
                                </Col>
                              </Row>
                            </Card.Header>
                            {renderCallListItems()}
                          </Card>
                        </Popover.Body>
                      </Popover>
                    }
                    placement="bottom"
                    rootClose
                    show={showPopover}
                    trigger="click"
                    onToggle={show => setShowPopover(show)}
                  >
                    <Button className="fw-normal fs-normal" variant="text-primary" onClick={handleDropdownClick}>
                      {index + 1} of {callListLength} Properties
                      <FontAwesomeIcon className="ms-1" icon={faCaretDown} />
                    </Button>
                  </OverlayTrigger>
                </div>
                <Button variant="text-primary" onClick={() => handleItemChange(1)}>
                  Next
                  <FontAwesomeIcon icon={faArrowRight} />
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowModal(true)}
                >
                  <FontAwesomeIcon icon={faDownload} />
                  Download
                </Button>
              </div>
              <div className="bg-light p-3 rounded border h-100 overflow-auto">
                <div className="d-flex flex-column bg-white rounded h-100 p-10 overflow-auto">
                  {initialLoad && (
                    <div className="d-flex h-100 justify-content-center align-items-center">
                      <Spinner animation="border" variant="secondary" />
                    </div>
                  )}
                  {header && <PreviewHeader header={header} />}
                  {previewBody && <PreviewBody body={previewBody} />}
                  {!initialLoad && !previewBody && (
                    <div className="d-flex h-100 justify-content-center align-items-center">
                      <Spinner animation="border" variant="secondary" />
                    </div>
                  )}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Container>
      </div>
      {showModal && (
        <DownloadModal
          callListId={callList.id}
          callListItemId={currentCallListItemId}
          show={showModal}
          templateId={template.id}
          onHide={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
